import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import api from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { MarkdownView } from "../components/MarkdownEditor";
import { Card } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { BookOpen, Home, LayoutDashboard, Loader2, LogOut, Printer, Receipt } from "lucide-react";
import { toast } from "sonner";

/**
 * HelpPage
 *
 * Renders the appropriate in-app manual for the current user. Two variants:
 *   - ``admin`` → GET /api/docs/admin-manual. Wrapped by ``AdminLayout``, so we
 *     only render the content (no extra header).
 *   - ``user`` → GET /api/docs/user-manual. Standalone page → we include its
 *     own header (same navigation as UserDashboard / UserOrdersPage).
 */
export default function HelpPage({ variant }) {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [text, setText] = useState(null);
  const isAdmin = variant === "admin" || (!variant && user?.role === "admin");
  const endpoint = isAdmin ? "/docs/admin-manual" : "/docs/user-manual";
  const title = isAdmin ? "Manual del administrador" : "Manual del alumno";

  useEffect(() => {
    setText(null);
    api
      .get(endpoint)
      .then(({ data }) => setText(data.text || ""))
      .catch((e) => toast.error(e?.response?.data?.detail || "No se pudo cargar el manual"));
  }, [endpoint]);

  const content = (
    <div className="max-w-3xl mx-auto" data-testid="help-page">
      <div className="mb-6 flex items-start justify-between gap-4 flex-wrap">
        <div>
          <div className="flex items-center gap-2 text-brand mb-1">
            <BookOpen className="w-4 h-4" />
            <span className="text-[10px] uppercase tracking-wider font-mono-data">Ayuda</span>
          </div>
          <h1 className="font-display text-4xl text-brand-ink">{title}</h1>
          <p className="text-sm text-gray-500 mt-2">
            Consulta rápida de todas las funciones de la plataforma. Si echas algo en falta,
            escríbenos a{" "}
            <a href="mailto:hablamos@sima.madrid" className="text-brand hover:underline">
              hablamos@sima.madrid
            </a>
            .
          </p>
        </div>
        <Button variant="outline" onClick={() => window.print()} data-testid="help-print">
          <Printer className="w-4 h-4 mr-2" /> Imprimir
        </Button>
      </div>

      <Card
        className="p-8 border border-gray-200 shadow-none rounded-lg prose-container print:border-0 print:p-0"
        data-testid="help-manual-content"
      >
        {text === null ? (
          <div className="flex items-center justify-center gap-2 text-gray-500 py-16">
            <Loader2 className="w-4 h-4 animate-spin" /> Cargando manual…
          </div>
        ) : text ? (
          <MarkdownView text={text} />
        ) : (
          <p className="text-gray-500 text-sm text-center py-10">
            El manual todavía no está disponible.{" "}
            <Link to={isAdmin ? "/admin" : "/dashboard"} className="text-brand hover:underline">
              Volver al panel
            </Link>
          </p>
        )}
      </Card>
    </div>
  );

  if (isAdmin) return content;

  return (
    <div className="min-h-screen bg-[#F9FAFB]" data-testid="user-help-page">
      <header className="bg-white border-b border-gray-200">
        <div className="max-w-5xl mx-auto px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <img src="/logo.jpg" alt="laclaseencasa" className="w-8 h-8 object-contain" />
            <span className="font-display text-lg text-brand-ink">laclaseencasa</span>
          </div>
          <div className="flex items-center gap-2">
            <span className="text-sm text-gray-600 hidden sm:inline">{user?.name}</span>
            <Link to="/dashboard/orders">
              <Button variant="ghost" size="sm">
                <Receipt className="w-4 h-4 mr-1" /> Mis pedidos
              </Button>
            </Link>
            <Link to="/dashboard">
              <Button variant="ghost" size="sm">
                <LayoutDashboard className="w-4 h-4 mr-1" /> Mi panel
              </Button>
            </Link>
            <Link to="/">
              <Button variant="ghost" size="sm">
                <Home className="w-4 h-4 mr-1" /> Home
              </Button>
            </Link>
            <Button
              variant="ghost"
              size="sm"
              onClick={() => { logout(); navigate("/login"); }}
            >
              <LogOut className="w-4 h-4 mr-1" /> Salir
            </Button>
          </div>
        </div>
      </header>
      <main className="max-w-5xl mx-auto px-6 py-10">{content}</main>
    </div>
  );
}
